"use client";

import Image from "next/image";
import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";

export type StageTone = "working" | "done" | "failed";

export interface StageAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

const BADGE: Record<StageTone, { label: string; className: string }> = {
  working: {
    label: "Working",
    className: "animate-pulse bg-[#FFF3DC] ring-[#E3D8C4] text-d-gray",
  },
  done: {
    label: "Done",
    className: "bg-[#EAF3DA] ring-[#C6DE95] text-[#4B6606]",
  },
  failed: {
    label: "Failed",
    className: "bg-[#FBE7E1] ring-[#F0BFB0] text-[#95341A]",
  },
};

function StageButton({
  action,
  variant,
}: {
  action: StageAction;
  variant: "d-primary" | "d-default";
}) {
  const className = "w-full justify-center px-[22px] py-3.5 h-12";

  if (action.href) {
    return (
      <Button asChild variant={variant} className={className}>
        <a href={action.href}>{action.label}</a>
      </Button>
    );
  }

  return (
    <Button variant={variant} className={className} onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

/** The white card that appears once a download is actually in flight. */
export function DownloadStage({
  tone,
  headline,
  subline,
  progress,
  primary,
  secondary,
}: {
  tone?: StageTone;
  headline: string;
  subline: string;
  /** undefined hides the bar; 0..100 fills it. */
  progress?: number;
  primary?: StageAction | null;
  secondary?: StageAction | null;
}) {
  const badge = tone ? BADGE[tone] : null;
  const hasActions = Boolean(primary || secondary);

  return (
    <div className="flex w-full flex-col gap-4 rounded-[24px] bg-white p-[22px] ring-1 ring-[#E3D8C4] lg:flex-row lg:items-center lg:gap-6 lg:rounded-[32px] lg:px-10 lg:py-9">
      <div className="flex items-center gap-3.5 lg:flex-1 lg:gap-6">
        <div className="relative flex size-[52px] shrink-0 items-center justify-center rounded-[16px] bg-[#FFF3DC] ring-1 ring-[#E3D8C4] lg:size-[76px] lg:rounded-[22px]">
          <Image
            src="/logo-small.svg"
            alt=""
            width={48}
            height={48}
            className="size-[34px] lg:size-12"
          />
          {tone === "failed" && (
            <span className="absolute -right-[5px] -bottom-[5px] flex size-[22px] items-center justify-center rounded-full bg-[#FBE7E1] text-[13px] font-extrabold text-[#95341A] ring-2 ring-white ring-inset lg:-right-1.5 lg:-bottom-1.5 lg:size-[26px] lg:text-[15px]">
              !
            </span>
          )}
          {tone === "done" && (
            <span className="absolute -right-[5px] -bottom-[5px] flex size-[22px] items-center justify-center rounded-full bg-[#EAF3DA] text-xs font-extrabold text-[#4B6606] ring-2 ring-white ring-inset lg:-right-1.5 lg:-bottom-1.5 lg:size-[26px] lg:text-sm">
              ✓
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-1 lg:flex-1 lg:gap-2">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[18px] font-semibold leading-6 text-d-black lg:text-[24px] lg:leading-8">
              {headline}
            </span>
            {badge && (
              <span
                className={cn(
                  "rounded-[8px] px-2.5 py-1 text-[13px] font-semibold leading-4 ring-1 ring-inset",
                  badge.className,
                )}
              >
                {badge.label}
              </span>
            )}
          </div>
          <span className="text-sm font-medium leading-[19px] text-d-gray lg:text-base lg:leading-[22px]">
            {subline}
          </span>
          {progress !== undefined && (
            <ProgressRow
              progress={progress}
              className="mt-1.5 hidden lg:flex"
            />
          )}
        </div>
      </div>

      {progress !== undefined && (
        <ProgressRow progress={progress} className="lg:hidden" />
      )}

      {hasActions && (
        <div className="flex shrink-0 flex-col gap-2.5 lg:min-w-[180px]">
          {primary && <StageButton action={primary} variant="d-primary" />}
          {secondary && <StageButton action={secondary} variant="d-default" />}
        </div>
      )}
    </div>
  );
}

function ProgressRow({
  progress,
  className,
}: {
  progress: number;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-3 lg:gap-4", className)}>
      <div className="h-[10px] flex-1 overflow-hidden rounded-[8px] bg-[#F3E4C9] ring-1 ring-inset ring-[#E3D8C4] lg:h-3">
        <div
          className="h-full rounded-[8px] bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0)_100%)] bg-[color:#FFD602] transition-[width] duration-250 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="min-w-[44px] text-right text-[15px] font-bold leading-[19px] text-d-black tabular-nums lg:min-w-[56px] lg:text-[18px] lg:leading-[22px]">
        {Math.floor(progress)}%
      </span>
    </div>
  );
}
